/**
 * Tempo relativo e rotulos de calendario para o retrato "Agora" e a projecao.
 *
 * Decisoes nao obvias:
 *  1. Toda diferenca e calculada contra uma BASE explicita (o `capturedAt` do
 *     servidor), nunca contra `Date.now()`: na previa o relogio do dado e
 *     ficticio e um "ha 3 anos" apareceria na tela. Em producao, a base e o
 *     relogio do servidor no instante do retrato, que e o que vale.
 *  2. Minutos inteiros, arredondados para baixo. "Ha 0 min" vira "agora".
 *  3. Nomes de mes e faixas de hora saem de `Intl` em pt-BR; nao ha tabela de
 *     strings a manter.
 */

const MES_LONGO = new Intl.DateTimeFormat('pt-BR', { month: 'long', year: 'numeric', timeZone: 'UTC' });
const DIA_MES = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: 'short' });

/** Minutos inteiros de `base` ate `alvo`; positivo quando `alvo` esta no futuro. */
export function minutosEntre(alvo: string, base: string): number | null {
  const a = new Date(alvo).getTime();
  const b = new Date(base).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) {
    return null;
  }
  return Math.trunc((a - b) / 60_000);
}

/** "há 4 min", "em 12 min", "há 2 h", "agora", ou "sem registro" para nulo. */
export function descreverRelativo(alvo: string | null, base: string): string {
  if (alvo === null) {
    return 'sem registro';
  }
  const minutos = minutosEntre(alvo, base);
  if (minutos === null) {
    return 'sem registro';
  }
  const abs = Math.abs(minutos);
  if (abs < 1) {
    return 'agora';
  }
  const prefixo = minutos > 0 ? 'em' : 'há';
  if (abs < 60) {
    return `${prefixo} ${abs} min`;
  }
  if (abs < 1_440) {
    return `${prefixo} ${Math.floor(abs / 60)} h`;
  }
  return `${prefixo} ${Math.floor(abs / 1_440)} d`;
}

/** 14 -> "14h–15h". */
export function faixaDaHora(hora: number): string {
  const inicio = Math.max(0, Math.min(23, Math.trunc(hora)));
  return `${inicio}h–${(inicio + 1) % 24}h`;
}

/** "2026-09" -> "setembro de 2026". */
export function nomeDoMes(monthKey: string): string {
  const [ano, mes] = monthKey.split('-').map(Number);
  if (ano === undefined || mes === undefined || Number.isNaN(ano) || Number.isNaN(mes)) {
    return monthKey;
  }
  return MES_LONGO.format(new Date(Date.UTC(ano, mes - 1, 1)));
}

/** Janela "25 de jul. – 24 de ago." a partir de dois ISO (o fim e exclusivo: mostra o dia anterior). */
export function descreverJanela(inicioIso: string, fimExclusivoIso: string): string {
  const inicio = new Date(inicioIso);
  const fim = new Date(new Date(fimExclusivoIso).getTime() - 60_000);
  if (Number.isNaN(inicio.getTime()) || Number.isNaN(fim.getTime())) {
    return 'período anterior';
  }
  return `${DIA_MES.format(inicio)} – ${DIA_MES.format(fim)}`;
}
